import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AppComponent} from "../../../app.component";
import {SharedDataService} from "../../../services/shared-data.service";
import {FS3ValidationResult} from "../../../objects/validationResult";
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {ListItem} from "../../../objects/listItem";
import {NewValidationResult} from "../../../objects/newValidationResult";
import {FS3Notification} from "../../../objects/notification";
import {parseYoutubeVideoID} from "../../../utils/parseUtils";
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'app-article-item-editor',
  templateUrl: './article-item-editor.component.html',
  styleUrls: ['./article-item-editor.component.scss']
})
export class ArticleItemEditorComponent implements OnInit, OnDestroy {

  @Input() application: AppComponent;
  @Input() payload: object;
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  private formValidationMessages = {
    required: 'Value is required.',
    titleRequired: 'Title is required.',
    textRequired: 'Text is required.',
  };
  public validationResult: NewValidationResult;
  public componentForm: FormGroup = new FormGroup({});
  public typeValue: FormControl = new FormControl('');
  public titleValue: FormControl = new FormControl('', [this.titleValidator()]);
  public textValue: FormControl = new FormControl('', [this.textValidator()]);
  public captionValue: FormControl = new FormControl('', [this.captionValidator()]);
  public videoValue: FormControl = new FormControl('', [this.videoValidator()]);
  public linkValue: FormControl = new FormControl('', [this.linkValidator()]);
  public linkTextValue: FormControl = new FormControl('', [this.linkTextValidator()]);
  public picture_src: string;
  public aspect_ratio: number;

  state: string = 'editing';
  errMsg: string = '';
  public articleData: object;
  public itemIdx: number;
  public itemType: string;
  public mode: string;
  public cdkAutosizeMinRows: number = 3;
  public cdkAutosizeMaxRows: number = 10;

  constructor(
    private sharedDataService: SharedDataService,
  ) { }

  ngOnInit(): void {
    this.articleData = this.payload['articleData']
    this.itemIdx = this.payload['idx']
    this.itemType = this.payload['articleData']['article'][this.itemIdx]['type']
    this.mode = this.payload['mode']
    this.typeValue.setValue(this.itemType);
    switch (this.itemType) {
      case 'title':
        this.titleValue.setValue(this.payload['articleData']['article'][this.itemIdx]['value']);
        break;
      case 'text':
        this.textValue.setValue(this.payload['articleData']['article'][this.itemIdx]['value']);
        this.cdkAutosizeMinRows = 10;
        this.cdkAutosizeMaxRows = 15;
        break;
      case 'image':
        this.captionValue.setValue(this.payload['articleData']['article'][this.itemIdx]['caption']);
        this.videoValue.setValue(this.payload['articleData']['article'][this.itemIdx]['video_url']);
        this.picture_src = this.payload['articleData']['article'][this.itemIdx]['image_url'];
        this.aspect_ratio = this.payload['articleData']['article'][this.itemIdx]['ratio'];
        break;
      case 'cta':
        this.captionValue.setValue(this.payload['articleData']['article'][this.itemIdx]['caption']);
        this.linkValue.setValue(this.payload['articleData']['article'][this.itemIdx]['link']);
        this.linkTextValue.setValue(this.payload['articleData']['article'][this.itemIdx]['link_text']);
        this.textValue.setValue(this.payload['articleData']['article'][this.itemIdx]['description']);
        this.picture_src = this.payload['articleData']['article'][this.itemIdx]['image_url'];
        this.aspect_ratio = this.payload['articleData']['article'][this.itemIdx]['ratio'];
        break;
    }
  }

  ngOnDestroy(): void {
  }

  public typeChanged(): boolean {
    this.itemType = this.typeValue.value;
    return true;
  }

  public fetchYoutubePicture(): boolean {
    const youtubeVideoID = parseYoutubeVideoID(this.videoValue.value);
    console.log(this.videoValue.value,youtubeVideoID);
    if(youtubeVideoID) {
      this.application.apiService
        .getYoutubeVideoMetaData(this.application.user.magic_key,youtubeVideoID)
        .subscribe(
          data => {
            console.log(data);
            this.picture_src = data['picture_src']
            this.aspect_ratio = data['aspect_ratio']
            if(!this.captionValue.value) {
              this.captionValue.setValue(data['title'])
            }
          },
          error => {
            const { exception } = error;
            if (exception && exception.error && exception.error.message) {
              let notification = new FS3Notification();
              notification.type = 'error';
              notification.text = exception.error.message;
              this.sharedDataService.pushNotification(notification);
            }
          }
        )

    }
    return true;
  }

  public handleSubmit(event: Event | void = null): boolean {
    if (event) {
      event.stopPropagation();
    }

    this.validationResult = new NewValidationResult();

    this.titleValue.updateValueAndValidity();
    this.textValue.updateValueAndValidity();
    this.captionValue.updateValueAndValidity();
    this.videoValue.updateValueAndValidity();
    this.linkValue.updateValueAndValidity();
    this.linkTextValue.updateValueAndValidity();

    if (!this.titleValue.valid) {
      const errorKey = Object.keys(this.titleValue.errors)[0]; // get error key from input errors
      this.validationResult.isValid = false;
      this.validationResult.messagesPerControls['titleValue'] = this.titleValue.errors[errorKey];
      return false;
    } else if (!this.textValue.valid) {
      const errorKey = Object.keys(this.textValue.errors)[0]; // get error key from input errors
      this.validationResult.isValid = false;
      this.validationResult.messagesPerControls['textValue'] = this.textValue.errors[errorKey];
      return false;
    } else {
      this.validationResult = null; //hide validationResult leftovers
    }

    this.state = 'loading';

    this.payload['articleData']['article'][this.itemIdx]['type'] = this.itemType;
    switch (this.itemType) {
      case 'title':
        this.payload['articleData']['article'][this.itemIdx]['value'] = this.titleValue.value;
        break;
      case 'text':
        this.payload['articleData']['article'][this.itemIdx]['value'] = this.textValue.value;
        break;
      case 'image':
        this.payload['articleData']['article'][this.itemIdx]['caption'] = this.captionValue.value;
        this.payload['articleData']['article'][this.itemIdx]['video_url'] = this.videoValue.value;
        this.payload['articleData']['article'][this.itemIdx]['image_url'] = this.picture_src;
        this.payload['articleData']['article'][this.itemIdx]['ratio'] = this.aspect_ratio;
        break;
      case 'cta':
        this.payload['articleData']['article'][this.itemIdx]['caption'] = this.captionValue.value;
        this.payload['articleData']['article'][this.itemIdx]['link'] = this.linkValue.value;
        this.payload['articleData']['article'][this.itemIdx]['link_text'] = this.linkTextValue.value;
        this.payload['articleData']['article'][this.itemIdx]['description'] = this.textValue.value;
        this.payload['articleData']['article'][this.itemIdx]['image_url'] = this.picture_src;
        this.payload['articleData']['article'][this.itemIdx]['ratio'] = this.aspect_ratio;
        break;
    }
    if(this.payload['doArticleInsert']) {
      this.doArticleInsert();
    } else {
      this.doArticleUpdate();
    }
    return false
  }

  private titleValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const value = control.value.toString().trim();
      const selectedItemType = this.typeValue.value;
      if(selectedItemType !== 'title') {
        return null;
      }
      if (!value) {
        return {required: this.formValidationMessages.titleRequired}
      }
      return null;
    };
  }
  private textValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const value = control.value.toString().trim();
      const selectedItemType = this.typeValue.value;
      if(selectedItemType !== 'text') {
        return null;
      }
      if (!value) {
        return {required: this.formValidationMessages.textRequired}
      }
      return null;
    };
  }
  private captionValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return null;
    };
  }
  private videoValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return null;
    };
  }
  private linkValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return null;
    };
  }
  private linkTextValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return null;
    };
  }

  private doArticleInsert() {
    let folderUUID = this.payload['folderUUID']
    let articleUUID = this.payload['articleData']['uuid'];
    let categoryIdx = this.payload['categoryIdx'];
    this.application.apiService
      .insertArticleItem(this.application.user.magic_key,articleUUID,categoryIdx,folderUUID,this.payload['articleData'])
      .subscribe(
        data => {
          let notification = new FS3Notification();
          notification.type = 'success';
          notification.text = 'Article has been created!';
          this.sharedDataService.pushNotification(notification);
          this.success.emit()
          //open newly created article in edit mode
          let articleUrl = '/articles/item/'+folderUUID+'/'+articleUUID
          articleUrl+= '/edit';
          this.application.router.navigateByUrl(articleUrl)
        },
        error => {
          const { exception } = error;
          if (exception && exception.error && exception.error.message) {
            let notification = new FS3Notification();
            notification.type = 'error';
            notification.text = exception.error.message;
            this.sharedDataService.pushNotification(notification);
            this.state = 'error';
          } else {
            //maybe close dialog
          }
        }
      )
  }

  private doArticleUpdate() {
    let folderUUID = this.application.router.url.split('/')[3]
    let articleUUID = this.application.router.url.split('/')[4]
    this.application.apiService
      .updateArticleItem(this.application.user.magic_key,articleUUID,folderUUID,this.payload['articleData'])
      .subscribe(
        data => {
          let notification = new FS3Notification();
          notification.type = 'success';
          notification.text = 'Article has been updated!';
          this.sharedDataService.pushNotification(notification);
          this.success.emit()
        },
        error => {
          const { exception } = error;
          if (exception && exception.error && exception.error.message) {
            let notification = new FS3Notification();
            notification.type = 'error';
            notification.text = exception.error.message;
            this.sharedDataService.pushNotification(notification);
            this.state = 'error';
          } else {
            //maybe close dialog
          }
        }
      )
  }

  public onNoClick(): void {
    //this.dialogRef.close({ isConfirmed: false });
    this.close.emit()
  }

}
